import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Property } from '@/types/property';

interface PricePerSqMeterChartProps {
  properties: Property[];
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';
}

interface PriceRange {
  range: string;
  count: number;
}

const PricePerSqMeterChart: React.FC<PricePerSqMeterChartProps> = ({ 
  properties,
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const calculatePricePerSqMeterRanges = (): PriceRange[] => {
    if (!properties || properties.length === 0) return [];

    const pricesPerSqMeter = properties.map(property => {
      const price = typeof property.valor_recente === 'string'
        ? parseFloat(property.valor_recente.replace(/[^\d.-]/g, ''))
        : property.valor_recente;
      
      // Parse area to number consistently
      let area: number;
      if (typeof property.area_util === 'string') {
        area = parseFloat(property.area_util);
      } else {
        area = Number(property.area_util);
      }
      
      // Use area_total if area_util is not available or invalid
      if (isNaN(area) || area <= 0) {
        if (typeof property.area_total === 'string') {
          area = parseFloat(property.area_total);
        } else {
          area = Number(property.area_total);
        }
      }
      
      if (!price || isNaN(area) || area <= 0) {
        return null;
      }
      
      return price / area;
    }).filter(val => val !== null) as number[];

    if (pricesPerSqMeter.length === 0) return [];

    const minPrice = Math.min(...pricesPerSqMeter);
    const maxPrice = Math.max(...pricesPerSqMeter);

    // Create appropriate ranges based on the dashboard type
    let ranges: { min: number; max: number; label: string }[];

    if (dashboardType === 'aluguel') {
      // For rentals, smaller ranges
      const maxPriceValue = Number(maxPrice);
      if (!isNaN(maxPriceValue) && maxPriceValue <= 100) {
        ranges = [
          { min: 0, max: 10, label: 'R$0 - R$10' },
          { min: 10, max: 20, label: 'R$10 - R$20' },
          { min: 20, max: 30, label: 'R$20 - R$30' },
          { min: 30, max: 50, label: 'R$30 - R$50' },
          { min: 50, max: 75, label: 'R$50 - R$75' },
          { min: 75, max: 100, label: 'R$75 - R$100' },
          { min: 100, max: Infinity, label: '> R$100' },
        ];
      } else {
        const rangeSize = Math.ceil((maxPrice - minPrice) / 7);
        ranges = Array.from({ length: 7 }, (_, i) => {
          const min = minPrice + rangeSize * i;
          const max = i === 6 ? Infinity : minPrice + rangeSize * (i + 1);
          return {
            min,
            max,
            label: i === 6 
              ? `> R$${Math.floor(min)}` 
              : `R$${Math.floor(min)} - R$${Math.floor(max)}`
          };
        });
      }
    } else {
      // For sales, larger ranges
      const maxPriceValue = Number(maxPrice);
      if (!isNaN(maxPriceValue) && maxPriceValue <= 25000) {
        ranges = [
          { min: 0, max: 5000, label: 'R$0 - R$5k' },
          { min: 5000, max: 7500, label: 'R$5k - R$7,5k' },
          { min: 7500, max: 10000, label: 'R$7,5k - R$10k' },
          { min: 10000, max: 15000, label: 'R$10k - R$15k' },
          { min: 15000, max: 20000, label: 'R$15k - R$20k' },
          { min: 20000, max: 25000, label: 'R$20k - R$25k' },
          { min: 25000, max: Infinity, label: '> R$25k' },
        ];
      } else {
        const rangeSize = Math.ceil((maxPrice - minPrice) / 7);
        ranges = Array.from({ length: 7 }, (_, i) => {
          const min = minPrice + rangeSize * i;
          const max = i === 6 ? Infinity : minPrice + rangeSize * (i + 1);
          return {
            min,
            max,
            label: i === 6 
              ? `> R$${Math.floor(min / 1000)}k` 
              : `R$${Math.floor(min / 1000)}k - R$${Math.floor(max / 1000)}k`
          };
        });
      }
    }

    return ranges.map(range => ({
      range: range.label,
      count: pricesPerSqMeter.filter(price => price >= range.min && price < range.max).length
    }));
  };

  const data = useMemo(calculatePricePerSqMeterRanges, [properties, dashboardType]);

  const COLORS = [
    '#3182CE', '#4299E1', '#63B3ED', '#90CDF4', 
    '#BEE3F8', '#EBF8FF', '#BEE3F8', '#90CDF4'
  ];

  if (data.length === 0) {
    return <div className="h-full flex items-center justify-center text-muted-foreground">Sem dados suficientes</div>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: 10, right: 0, left: 0, bottom: 40 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="range" 
          angle={-45} 
          textAnchor="end" 
          height={80} 
          tick={{ fontSize: isMobile ? 9 : 12 }}
        />
        <YAxis 
          allowDecimals={false} 
          tick={{ fontSize: isMobile ? 10 : 12 }}
        />
        <Tooltip
          formatter={(value) => [`${value} ${value === 1 ? 'imóvel' : 'imóveis'}`, `Quantidade`]}
          labelFormatter={(label) => `Faixa: ${label}`}
        />
        <Bar dataKey="count" name="Quantidade" radius={[4, 4, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PricePerSqMeterChart;
